import { Scale } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";
import { EyebrowBadge } from "@/src/common/components/eyebrow-badge";
import { GradientOrb } from "@/src/common/components/gradient-orb";
import type { LegalDocumentKey } from "@/src/legal/constants/legal-content";
import { LEGAL_PAGES } from "@/src/legal/constants/legal-routes";

interface LegalHeroProps {
	documentKey: LegalDocumentKey;
}

export function LegalHero({ documentKey }: LegalHeroProps) {
	const { t } = useTranslation("legal");

	return (
		<section className="relative isolate overflow-hidden pt-16 pb-10 md:pt-24 md:pb-14">
			<div aria-hidden="true" className="gradient-mesh absolute inset-0 -z-10 opacity-40" />
			<GradientOrb intensity="soft" className="top-10 right-1/3 size-72" />

			<div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
				<EyebrowBadge icon={<Scale className="h-3.5 w-3.5" />}>{t("eyebrow")}</EyebrowBadge>
				<h1 className="mt-5 text-balance text-4xl font-semibold tracking-tight text-foreground md:text-5xl">{t(`${documentKey}.title`)}</h1>
				<p className="mt-3 text-sm text-default-500">{t(`${documentKey}.updated`)}</p>

				<nav aria-label={t("nav.label")} className="no-print mt-8 flex flex-wrap gap-2">
					{LEGAL_PAGES.map((page) => (
						<NavLink
							key={page.key}
							to={page.to}
							end
							className={({ isActive }) =>
								`rounded-full border px-4 py-1.5 text-sm font-medium transition ${
									isActive
										? "border-brand-500 bg-brand-500 text-white shadow-md shadow-brand-500/20"
										: "border-default-200 bg-content1/80 text-default-600 hover:border-default-400 hover:text-foreground"
								}`
							}
						>
							{t(`nav.${page.key}`)}
						</NavLink>
					))}
				</nav>
			</div>
		</section>
	);
}
